import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Input } from './input';

export default function Autocomplete({
  value,
  onValueChange,
  endpoint,
  options: optionsProp,
  searchParam = 'search',
  getOptionLabel,
  getOptionValue,
  placeholder = 'Selecione...',
  debounceMs = 300,
  minChars = 1,
  limit = 15,
  extraParams = {},
  emptyMessage = 'Nenhum resultado encontrado',
  selectedLabel: selectedLabelProp = '',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const extraParamsRef = useRef(extraParams);
  useEffect(() => { extraParamsRef.current = extraParams; }, [extraParams]);

  const optionsRef = useRef(optionsProp);
  useEffect(() => { optionsRef.current = optionsProp; }, [optionsProp]);
  const getOptionLabelRef = useRef(getOptionLabel);
  useEffect(() => { getOptionLabelRef.current = getOptionLabel; }, [getOptionLabel]);
  const getOptionValueRef = useRef(getOptionValue);
  useEffect(() => { getOptionValueRef.current = getOptionValue; }, [getOptionValue]);

  const isLocal = !!optionsProp;

  const fetchResults = useCallback(async (term) => {
    if (isLocal) {
      if (!optionsRef.current) { setResults([]); return; }
      const termLower = term.toLowerCase();
      const filtered = optionsRef.current.filter((item) => {
        const label = getOptionLabelRef.current(item).toLowerCase();
        const val = getOptionValueRef.current(item).toLowerCase();
        return label.includes(termLower) || val.includes(termLower);
      });
      setResults(filtered.slice(0, limit));
      return;
    }
    if (term.length < minChars) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(endpoint, {
        params: { [searchParam]: term, limit, ...extraParamsRef.current },
      });
      setResults(r.data?.data || r.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint, searchParam, limit, minChars, isLocal]);

  const handleSearchChange = useCallback((val) => {
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(val), debounceMs);
  }, [fetchResults, debounceMs]);

  useEffect(() => {
    if (!isLocal) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchResults(search), debounceMs);
      return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }
  }, [search, fetchResults, debounceMs, isLocal]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (open && isLocal) fetchResults(search);
  }, [open, isLocal, fetchResults, search]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (item) => {
    onValueChange(getOptionValue(item), item);
    setSelectedLabel(getOptionLabel(item));
    setSearch('');
    setOpen(false);
  };

  useEffect(() => {
    if (!value) setSelectedLabel(null);
  }, [value]);

  const showAll = isLocal && search.length < (minChars || 1);
  const hasMinChars = isLocal || search.length >= minChars;
  const displayResults = showAll ? (optionsProp || []) : results;

  return (
    <div className="relative" ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between font-normal"
        onClick={() => setOpen(!open)}
      >
        {value && (selectedLabel || selectedLabelProp)
          ? selectedLabel || selectedLabelProp
          : <span className="text-muted-foreground">{placeholder}</span>}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 rounded-md border bg-popover shadow-md">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Digite para buscar..."
                className="pl-8"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {loading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loading && hasMinChars && displayResults.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
            )}
            {!loading && displayResults.length > 0 && (
              <div>
                {displayResults.map((item) => (
                  <button
                    key={getOptionValue(item)}
                    type="button"
                    className={cn(
                      'w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent hover:text-accent-foreground cursor-pointer',
                      value === getOptionValue(item) && 'bg-accent'
                    )}
                    onClick={() => handleSelect(item)}
                  >
                    <Check
                      className={cn(
                        'h-4 w-4 shrink-0',
                        value === getOptionValue(item) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {getOptionLabel(item)}
                  </button>
                ))}
              </div>
            )}
            {!loading && !isLocal && search.length < minChars && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Digite pelo menos {minChars} caractere{minChars > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
